export interface SchedulingTransactionInput {
  previousLinkedQuoteId: string;
  previousAppointmentTime: string | undefined;
  scheduledAtIso: string;
  repName: string;
  appointmentId: string;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  activityNotes: string;
  stageHistory: unknown[];
  activities: unknown[];
}

export interface SchedulingTransactionDeps {
  ensureCustomer: () => Promise<void>;
  resolveCanonicalLeadId: () => Promise<string | null>;
  ensureQuote: (appointmentId: string) => Promise<string | null>;
  updateLead: (leadId: string, updates: Record<string, unknown>) => Promise<{ error?: unknown }>;
  createAppointment: (payload: {
    appointmentId: string;
    leadId: string;
    quoteId: string;
    scheduledAt: string;
    status: "scheduled";
    contactName: string;
    contactPhone: string;
    contactEmail?: string;
    createdBy: string;
    notes: string;
  }) => Promise<{ error?: unknown }>;
  deleteQuote: (quoteId: string) => Promise<void>;
}

export interface SchedulingTransactionResult {
  quoteId: string;
  newlyCreatedQuote: boolean;
}

export async function scheduleAppointmentTransactional(
  deps: SchedulingTransactionDeps,
  input: SchedulingTransactionInput,
): Promise<SchedulingTransactionResult> {
  const previousLinkedQuoteId = input.previousLinkedQuoteId;
  const previousAppointmentTime = input.previousAppointmentTime;

  let canonicalLeadId: string | null = null;
  let quoteId: string | null = null;
  let newlyCreatedQuote = false;

  try {
    await deps.ensureCustomer();

    canonicalLeadId = await deps.resolveCanonicalLeadId();
    if (!canonicalLeadId) throw new Error("Failed to resolve canonical lead id");

    quoteId = await deps.ensureQuote(input.appointmentId);
    if (!quoteId) throw new Error("Failed to ensure quote for scheduled appointment");
    newlyCreatedQuote = !previousLinkedQuoteId || previousLinkedQuoteId !== quoteId;

    const leadUpdateResult = await deps.updateLead(canonicalLeadId, {
      stage: "Appointment Scheduled",
      appointment_time: input.scheduledAtIso,
      linked_quote_id: quoteId,
      stage_history: input.stageHistory,
      activities: input.activities,
      updated_at: new Date().toISOString(),
    });
    if (leadUpdateResult.error) throw leadUpdateResult.error;

    const appointmentResult = await deps.createAppointment({
      appointmentId: input.appointmentId,
      leadId: canonicalLeadId,
      quoteId,
      scheduledAt: input.scheduledAtIso,
      status: "scheduled",
      contactName: input.contactName,
      contactPhone: input.contactPhone,
      contactEmail: input.contactEmail,
      createdBy: input.repName,
      notes: input.activityNotes,
    });
    if (appointmentResult.error) throw appointmentResult.error;

    return { quoteId, newlyCreatedQuote };
  } catch (error) {
    if (canonicalLeadId) {
      await deps.updateLead(canonicalLeadId, {
        linked_quote_id: previousLinkedQuoteId || null,
        appointment_time: previousAppointmentTime || null,
      });
    }
    if (quoteId && newlyCreatedQuote) {
      await deps.deleteQuote(quoteId);
    }
    throw error;
  }
}
